/**
 * Register API data source wizard — pure presentation on WizardShell (AVA-602 showcase D, plan N29):
 * rail steps, the rail summary box and info rows per step, footer hints and the Review cards.
 */
import { REGISTER_KIND, isGroupRegister, resultingMode } from '../../../modules/registerImportWizardModel.js';
import {
  API_STEP,
  frequencyLabel,
  isScheduled,
} from '../../../modules/apiSourceRules.js';

const fmt = (value) => Number(value || 0).toLocaleString('en-US');

const AUTH_TYPES = ['None', 'API Key', 'Bearer Token', 'Basic Auth', 'OAuth2 (Client Credentials)'];
const PAGINATION_TYPES = ['None', 'Page Number', 'Offset / Limit', 'Cursor-Based'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad = (n) => String(Number(n) || 0).padStart(2, '0');

/** WizardShell steps: rail title/description + pane heading/subheading. */
export function apiWizardSteps() {
  return [
    { title: 'Configure API', description: 'Connection settings', heading: 'Configure API', subheading: 'Where the records come from and how AVA signs in' },
    { title: 'Test & Preview', description: 'Verify connection', heading: 'Test & Preview', subheading: 'Check the connection and look at the first records the API returns' },
    { title: 'Map Fields', description: 'Match columns', heading: 'Map Fields', subheading: 'Name the register field for each API column, then set its type and role' },
    { title: 'Schedule', description: 'Auto-pull settings', heading: 'Schedule', subheading: 'How often the register pulls from this source' },
    { title: 'Review', description: 'Confirm & save', heading: 'Review', subheading: 'Check the source before it is saved' },
  ];
}

/** Progress 0–100 across the five steps. */
export function apiWizardProgress(step) {
  const total = apiWizardSteps().length;
  return Math.round(((Math.min(Math.max(step, 1), total) - 1) / (total - 1)) * 100);
}

/** Primary footer label per step. */
export function primaryLabel(step, isEdit) {
  switch (step) {
    case API_STEP.CONFIGURE: return 'Continue to Test & Preview';
    case API_STEP.TEST: return 'Continue to Map Fields';
    case API_STEP.MAP: return 'Continue to Schedule';
    case API_STEP.SCHEDULE: return 'Continue to Review';
    default: return isEdit ? 'Update' : 'Save & Activate';
  }
}

/** Human schedule line: "Daily at 06:00 UTC". */
export function scheduleSummary(schedule) {
  const s = schedule || {};
  const time = `${pad(s.hour)}:${pad(s.minute)} UTC`;
  switch (Number(s.frequency || 0)) {
    case 1: return `Data will be pulled every hour at :${pad(s.minute)} past the hour.`;
    case 2: return `Data will be pulled daily at ${time}.`;
    case 3: return `Data will be pulled every ${DAYS[Number(s.dayOfWeek) || 0]} at ${time}.`;
    case 4: return s.cronExpression ? `Custom schedule: ${s.cronExpression}` : '';
    default: return 'Data will only be pulled when triggered manually.';
  }
}

/** Short frequency chip text for Review: "Daily · 06:00 UTC". */
export function frequencyChip(schedule) {
  const s = schedule || {};
  const label = frequencyLabel(s.frequency);
  switch (Number(s.frequency || 0)) {
    case 1: return `${label} · :${pad(s.minute)}`;
    case 2: return `${label} · ${pad(s.hour)}:${pad(s.minute)} UTC`;
    case 3: return `${label} · ${(DAYS[Number(s.dayOfWeek) || 0] || '').slice(0, 3)} ${pad(s.hour)}:${pad(s.minute)} UTC`;
    case 4: return s.cronExpression ? `${label} · ${s.cronExpression}` : label;
    default: return label;
  }
}

function firstRunText(schedule) {
  if (schedule && schedule.triggerImmediate) return 'On save';
  return isScheduled(schedule && schedule.frequency) ? 'Next schedule' : 'When triggered';
}

/**
 * Rail summary box per step (ImportRailSummary `stats`).
 * @param {object} s - { step, testResult, previewData, mappingStatus, mapping, schedule, hasIdentity,
 *   identityLabel, registerKind, saveBlockerCount, fieldCount }
 */
export function apiRailStats(s) {
  if (s.step === API_STEP.TEST) {
    const preview = s.previewData;
    const test = s.testResult;
    if (!preview && !(test && test.success)) return null;
    const rows = [];
    if (preview) {
      const shown = Array.isArray(preview.data) ? preview.data.length : 0;
      rows.push({ label: 'Rows shown', value: preview.totalRecords ? `${fmt(shown)} of ${fmt(preview.totalRecords)}` : fmt(shown), tone: 'hl' });
      rows.push({ label: 'Columns', value: fmt((preview.headers || []).length) });
    }
    if (test && test.success) rows.push({ label: 'Response', value: `${test.statusCode} · ${fmt(test.responseTimeMs)} ms` });
    return { title: 'Preview', rows };
  }

  if (s.step === API_STEP.MAP) {
    const m = s.mappingStatus;
    if (!m) return null;
    const scheduled = isScheduled(s.schedule && s.schedule.frequency);
    const unset = s.registerKind === REGISTER_KIND.GROUP_UNSET;
    const state = m.facilityColumnState;
    return {
      title: 'Mapping',
      rows: [
        { label: 'Fields configured', value: `${m.configured} / ${m.totalColumns}`, tone: m.configured ? 'hl' : '' },
        { label: 'Tag column', value: m.tagColumn || 'Not set', tone: m.tagColumn ? '' : 'muted' },
        m.facilityColumn
          ? { label: 'Facility column', value: m.facilityColumn }
          : { label: 'Facility column', value: state ? state.value : 'Not set', tone: state ? state.tone : 'muted' },
        {
          label: 'Identity',
          value: m.identity || 'Not set',
          tone: m.identity ? '' : (scheduled && !unset ? 'er' : (m.configured ? 'wn' : 'muted')),
        },
      ],
    };
  }

  if (s.step === API_STEP.SCHEDULE || s.step === API_STEP.REVIEW) {
    const scheduled = isScheduled(s.schedule && s.schedule.frequency);
    const unset = s.registerKind === REGISTER_KIND.GROUP_UNSET;
    const rows = [
      { label: 'Frequency', value: frequencyLabel(s.schedule && s.schedule.frequency), tone: scheduled && (!s.hasIdentity || unset) ? 'er' : '' },
      { label: 'First run', value: firstRunText(s.schedule) },
      { label: 'Identity', value: s.identityLabel || 'Not set', tone: s.hasIdentity ? '' : 'wn' },
    ];
    if (s.step === API_STEP.REVIEW) {
      rows.unshift({ label: 'Fields mapped', value: fmt(s.fieldCount), tone: s.fieldCount ? 'hl' : '' });
      if (s.saveBlockerCount) rows.push({ label: 'Blocking issues', value: fmt(s.saveBlockerCount), tone: 'er' });
    }
    return { title: s.step === API_STEP.REVIEW ? 'Ready to Save' : 'Schedule', rows };
  }
  return null;
}

/**
 * Rail info rows: owner (Facility / Facility Group), register mode, and the source once named.
 * @param {object} s - { register, registerKind, registerRowCount, hasFacilityColumn, sourceName, step }
 */
export function apiRailInfoRows(s) {
  const reg = s.register || {};
  const rows = [];
  if (isGroupRegister(reg)) {
    const group = reg.facilityGroup && reg.facilityGroup.name;
    rows.push({ icon: 'mdi-office-building-marker-outline', text: group ? `Facility Group · ${group}` : 'Facility Group register' });
    if (s.registerKind === REGISTER_KIND.GROUP_UNSET) {
      rows.push({ icon: 'mdi-lock-alert-outline', text: 'Mode not set', title: `Has ${fmt(s.registerRowCount)} rows but no Shared/Separated mode` });
    } else if (s.registerKind === REGISTER_KIND.GROUP_SHARED) {
      rows.push({ icon: 'mdi-share-variant-outline', text: 'Shared register', title: 'Every Facility in the Facility Group sees all rows' });
    } else if (s.registerKind === REGISTER_KIND.GROUP_SEPARATED) {
      rows.push({ icon: 'mdi-call-split', text: 'Separated register', title: 'Each Facility sees only its rows' });
    } else if (s.registerKind === REGISTER_KIND.GROUP_EMPTY) {
      const mode = resultingMode(s.registerKind, !!s.hasFacilityColumn);
      rows.push({
        icon: mode === 'Separated' ? 'mdi-call-split' : 'mdi-share-variant-outline',
        text: `${mode} · set by the first run`,
        title: 'Empty register — the first accepted run sets the mode',
      });
    }
  } else {
    const facility = reg.facility && reg.facility.name;
    rows.push({ icon: 'mdi-domain', text: facility ? `Facility · ${facility}` : 'Facility register' });
  }
  if (s.step > API_STEP.CONFIGURE && s.sourceName) rows.push({ icon: 'mdi-api', text: s.sourceName });
  return rows;
}

/**
 * The single hint next to the footer's primary action.
 * @param {object} s - { step, canContinue, testResult, previewData, mappingHint, schedule, hasIdentity,
 *   registerKind, scheduleInvalid, saveBlockerCount }
 * @returns {?{kind:'err'|'warn'|'info', icon:string, text:string}}
 */
export function apiFooterHint(s) {
  switch (s.step) {
    case API_STEP.CONFIGURE:
      return s.canContinue ? null : { kind: 'info', icon: 'mdi-information-outline', text: 'Name the source and enter its URL' };
    case API_STEP.TEST:
      return s.testResult && s.testResult.success ? null : { kind: 'info', icon: 'mdi-connection', text: 'Test the connection to continue' };
    case API_STEP.MAP: {
      if (!s.canContinue && s.mappingHint) return s.mappingHint;
      if (!s.canContinue) return { kind: 'err', icon: 'mdi-alert-circle-outline', text: 'Configure at least one field name' };
      const scheduled = isScheduled(s.schedule && s.schedule.frequency);
      if (scheduled && !s.hasIdentity && s.registerKind !== REGISTER_KIND.GROUP_UNSET) {
        return { kind: 'warn', icon: 'mdi-fingerprint-off', text: `${frequencyLabel(s.schedule.frequency)} needs an identity column — or choose Manual Only next` };
      }
      return null;
    }
    case API_STEP.SCHEDULE:
      return s.scheduleInvalid ? { kind: 'err', icon: 'mdi-alert-circle-outline', text: 'Pick an identity column or choose Manual Only' } : null;
    case API_STEP.REVIEW:
      return s.saveBlockerCount
        ? { kind: 'err', icon: 'mdi-alert-circle-outline', text: `${s.saveBlockerCount} ${s.saveBlockerCount === 1 ? 'issue blocks' : 'issues block'} saving` }
        : null;
    default:
      return null;
  }
}

/**
 * Review cards (rows are { label, value, tone?, mono?, method? }).
 * @param {object} s - { config, mapping, schedule, register, registerKind, hasIdentity, identityLabel,
 *   fieldCount, identityBlocked }
 */
export function reviewCards(s) {
  const config = s.config || {};
  const mapping = s.mapping || {};
  const schedule = s.schedule || {};
  const pagination = config.pagination || {};
  const headers = (config.headers || []).filter((h) => h && h.key);
  const reg = s.register || {};
  const scheduled = isScheduled(schedule.frequency);
  const aliasCount = (mapping.facilityValueMappings || []).length;

  let facility;
  switch (s.registerKind) {
    case REGISTER_KIND.GROUP_SEPARATED:
    case REGISTER_KIND.GROUP_EMPTY:
      facility = mapping.facilityColumn
        ? { value: `Column ${mapping.facilityColumn}${aliasCount ? ` · ${aliasCount} Facility name ${aliasCount === 1 ? 'mapping' : 'mappings'}` : ''}` }
        : { value: s.registerKind === REGISTER_KIND.GROUP_EMPTY ? 'None — the first run makes the register Shared' : 'Not set', tone: s.registerKind === REGISTER_KIND.GROUP_SEPARATED ? 'err' : '' };
      break;
    case REGISTER_KIND.GROUP_SHARED:
      facility = { value: 'Shared — rows at Facility Group level' };
      break;
    case REGISTER_KIND.GROUP_UNSET:
      facility = { value: 'Mode not set — pulls refused', tone: 'warn' };
      break;
    default:
      facility = { value: `Every row belongs to ${(reg.facility && reg.facility.name) || 'this register’s Facility'}` };
  }

  const pagingText = PAGINATION_TYPES[Number(pagination.type) || 0] || 'None';
  return [
    {
      key: 'config',
      icon: 'mdi-api',
      title: 'API configuration',
      step: API_STEP.CONFIGURE,
      rows: [
        { label: 'Name', value: config.name || '—' },
        { label: 'URL', value: config.url || '—', mono: true, method: config.httpMethod || 'GET' },
        { label: 'Authentication', value: AUTH_TYPES[Number(config.auth && config.auth.authType) || 0] || 'None' },
        { label: 'Data path', value: config.responseDataPath || '(root)', mono: true },
        { label: 'Pagination', value: pagingText },
        { label: 'Headers', value: headers.length ? `${headers.length} custom ${headers.length === 1 ? 'header' : 'headers'}` : 'None' },
      ],
    },
    {
      key: 'mapping',
      icon: 'mdi-link-variant',
      title: 'Column mapping',
      step: API_STEP.MAP,
      rows: [
        { label: 'Fields mapped', value: fmt(s.fieldCount), tone: s.fieldCount ? '' : 'err' },
        { label: 'Identity', value: s.identityLabel || 'Not set', tone: s.hasIdentity ? '' : (scheduled ? 'err' : 'warn') },
        { label: 'Tag column', value: mapping.tagColumn || 'Not set' },
        { label: 'Facility', ...facility },
      ],
    },
    {
      key: 'schedule',
      icon: 'mdi-clock-outline',
      title: 'Schedule',
      step: API_STEP.SCHEDULE,
      rows: [
        { label: 'Frequency', value: frequencyChip(schedule), chip: true, manual: !scheduled, tone: s.identityBlocked ? 'err' : '' },
        { label: 'First run', value: schedule.triggerImmediate ? 'Immediately after saving' : (scheduled ? 'At the next scheduled time' : 'When triggered manually') },
      ],
    },
  ];
}
